import fetchIntercept from "fetch-intercept";
import type { FetchInterceptorResponse } from "fetch-intercept";
import { apiDomain } from "configs/configs";

type RequestConfig = RequestInit & {
  method?: string;
};

interface IDataRequest {
  [key: string]: RequestConfig;
}

interface ICreateInterceptors {
  getInstance: () => { registerInterceptors: () => void };
}

const refresh = async () => {
  const url = `${apiDomain}/refresh`;
  const options: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  };

  return await fetch(url, options);
};

export const CreateInterceptors: ICreateInterceptors = (() => {
  let instance: ReturnType<typeof init> | null = null;
  let isRefreshing = false;
  let refreshSubscribers: ((isRefereshError?: boolean) => void)[] = [];
  let dataRequests: IDataRequest = {};

  const subscribeTokenRefresh = (
    callback: (isRefereshError?: boolean) => void
  ): void => {
    refreshSubscribers.push(callback);
  };

  const onRefreshed = (isRefereshError?: boolean): void => {
    refreshSubscribers.forEach((callback) => callback(isRefereshError));
    refreshSubscribers = [];
  };

  const removeDataRequestsItem = (requestKey: string): void => {
    const { [requestKey]: _omit, ...remaining } = dataRequests;
    dataRequests = remaining;
  };

  const getRelativeUrl = (url: string): string =>
    url.replace(window.location.origin, "");

  const init = () => ({
    registerInterceptors: (): void => {
      fetchIntercept.register({
        request(url: string, config: RequestConfig) {
          if (config && url.indexOf("REFRESH_URL") === -1) {
            dataRequests = {
              ...dataRequests,
              [`${getRelativeUrl(url)}_${config.method || "GET"}`]: config,
            };
          }
          return [url, config];
        },

        response(response: FetchInterceptorResponse) {
          const requestKey = `${getRelativeUrl(response.url)}_${
            response.request?.method || "GET"
          }`;

          if (
            response.status === 404 &&
            response.url.indexOf("REFRESH_URL") === -1
          ) {
            if (!isRefreshing) {
              isRefreshing = true;
              refresh()
                .then((refreshRequestResponse) => {
                  if (refreshRequestResponse.status === 403) {
                    onRefreshed(true);
                  }

                  isRefreshing = false;
                  onRefreshed();
                })
                .catch(() => {
                  onRefreshed(true);
                });
            }

            const retryOriginalRequest: any = new Promise((resolve, reject) => {
              subscribeTokenRefresh((isRefereshError?: boolean) => {
                if (isRefereshError) {
                  reject("Refresh session failed");

                  return;
                }

                fetch(response.url, {
                  ...dataRequests[requestKey],
                })
                  .then((originalRequestResponse) => {
                    resolve(originalRequestResponse);
                    removeDataRequestsItem(requestKey);
                  })
                  .catch((err) => {
                    reject(err);
                  });
              });
            });

            return retryOriginalRequest;
          }

          removeDataRequestsItem(requestKey);
          return response;
        },
      });
    },
  });

  return {
    getInstance() {
      if (!instance) {
        instance = init();
      }
      return instance;
    },
  };
})();
